import { useState } from "react";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { IMcpToken, IMcpTokenCreated } from "../models/mcpModels";

// The MCP server lives on the same host as the API, at "/mcp" (the API base ends in "/api").
// An explicit VITE_MCP_SERVER_URL wins if set (e.g. a future branded mcp.yourplanny.com).
const deriveServerUrl = (): string => {
  const override = import.meta.env.VITE_MCP_SERVER_URL as string | undefined;
  if (override) {
    return override;
  }
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
  const host = apiBase.replace(/\/api\/?$/, "");
  return host ? `${host}/mcp` : "/mcp";
};

export const useMcpSection = () => {
  const [name, setName] = useState("");
  const [newToken, setNewToken] = useState<IMcpTokenCreated | null>(null);

  const {
    data: tokens,
    refetch,
    isFetching,
  } = useApiQuery<IMcpToken[]>({
    queryKey: [endpointsMetadata.mcp.getTokens],
    endpoint: endpointsMetadata.mcp.getTokens,
    globalIsLoading: false,
    retry: false,
  });

  const { mutate: generate, isLoading: isGenerating } =
    useApiMutation<IMcpTokenCreated>({
      onSuccess: (data) => {
        setNewToken(data);
        setName("");
        refetch();
      },
    });

  const { mutate: revoke, isLoading: isRevoking } = useApiMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const handleGenerate = () => {
    generate({
      endpoint: endpointsMetadata.mcp.generateToken,
      method: "post",
      payload: { name: name.trim() ? name.trim() : undefined },
      disableSuccessToast: true,
    });
  };

  const handleRevoke = (token: IMcpToken) => {
    showCustomConfirmDialog(
      `Сигурни ли сте, че искате да премахнете достъпа „${token.name}"? Всеки AI клиент, който използва този токен, ще спре да работи веднага.`,
      () =>
        revoke({
          endpoint: endpointsMetadata.mcp.revokeToken,
          method: "delete",
          queryParams: { id: token.id },
          successMessage: "Достъпът беше премахнат.",
        }),
      () => {},
      "warning",
      "Да, премахни",
      "Отказ"
    );
  };

  return {
    serverUrl: deriveServerUrl(),
    tokens: tokens ?? [],
    isFetching,
    isGenerating,
    isRevoking,
    name,
    setName,
    newToken,
    dismissNewToken: () => setNewToken(null),
    handleGenerate,
    handleRevoke,
  };
};
